import {useMemo} from "react";
import {TextFieldElement} from "react-hook-form-mui";
import {
  Alert,
  styled,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography
} from "@mui/material";
import {Box} from "@mui/system";

import {getAgeGroups, getDemographicsSummary} from "@components/Pages/Events/eventFunctions";
import RadioButtonGroup from "@components/common/HookFormMui/RadioButtonGroup";
import {demographicSources, eventDemographics as demographicCategories} from "@lib/constants";
import {
  preventNonNumericKeyPress,
  validatePositiveInteger
} from "@lib/utils";
import CompleteEventFields from '@components/ContentComponents/Events/CompleteEvent/CompleteEventFields';

const DataHeading = styled(Typography)({
  fontSize: '16px',
  lineHeight: '40px',
});

const TableHeadCell = styled(TableCell)({
  backgroundColor: '#f4f4f4',
  fontWeight: 'bold',
  padding: '4px 16px',
  textAlign: 'center',
});

const NumberField = styled(TextFieldElement)({
  input: {
    textAlign: 'center',
    // We add extra padding to the left side of the input element, to move the value closer to the center. Otherwise,
    // the built-in up-down arrows of input type="number" make it look like it's skewed to the left.
    padding: '12px 10px 12px 24px',
    maxWidth: '80px'
  },
});

/**
 * Form fields for the Event Demographics form. This does not include a <FormContainer> or any submit logic,
 * so it can be used in a form by itself, or as part of another, larger form.
 */
const EventDemographicsFormFields = ({form, event, showSummary = false, showCompleteConfirmation = false }) => {
  const ageRanges = getAgeGroups(event);

  const {
    watch
  } = form;

  const valuesWatch = watch();

  // Update the 'sums must match' messages when values change. These are separate from the regular form field
  // validation, and appear even if the form hasn't been submitted yet.
  const demographicsMessages = useMemo(() => {
    if (!showSummary) {
      return null;
    }
    const summary = getDemographicsSummary(event, valuesWatch);
    return {
      age: !summary.groupsValid.age ? `The sum of age group categories must equal ${summary.participants}. The sum is
          currently ${summary.sums.age}.` : '',
      gender: !summary.groupsValid.gender ? `The sum of gender categories must equal ${summary.participants}. The sum is
          currently ${summary.sums.gender}.` : '',
      race: !summary.groupsValid.race ? `The sum of race and ethnicity categories must be equal to or greater
          than ${summary.participants}. The sum is currently ${summary.sums.race}.` : '',
    };
  }, [event, showSummary, valuesWatch]);

  return (
    <Box mt="28px">
      <DataHeading variant="h4">
        AGE GROUP
      </DataHeading>
      {demographicsMessages?.age && (
        <Alert severity="error" sx={{mb: 2}}>
          {demographicsMessages.age}
        </Alert>
      )}
      <Table sx={{
        width: 'auto'
      }} data-cy="table-age">
        <TableHead>
          <TableRow>
            {ageRanges.map(ageRange => (
              <TableHeadCell key={`th-${ageRange.id}`}>
                <label className={'required'} htmlFor={`age.${ageRange.id}`}>{ageRange.label}</label>
              </TableHeadCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          <TableRow>
            {ageRanges.map(ageRange => (
              <TableCell key={`td-${ageRange.id}`} align="center">
                <NumberField
                  id={`field-age-${ageRange.id}`}
                  name={`age.${ageRange.id}`}
                  label=""
                  type="number"
                  control={form.control}
                  required
                  validation={validatePositiveInteger}
                  onKeyDown={preventNonNumericKeyPress}
                  data-cy="field-age"
                />
              </TableCell>
            ))}
          </TableRow>
        </TableBody>
      </Table>

      <DataHeading variant="h4">
        GENDER
      </DataHeading>
      {demographicsMessages?.gender && (
        <Alert severity="error" sx={{mb: 2}}>
          {demographicsMessages.gender}
        </Alert>
      )}
      <Table sx={{
        width: 'auto'
      }} data-cy="table-gender">
        <TableHead>
          <TableRow>
            {demographicCategories.gender.map(gender => (
              <TableHeadCell key={`th-${gender.id}`}>
                <label className={'required'} htmlFor={`gender.${gender.id}`}>{gender.label}</label>
              </TableHeadCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          <TableRow>
            {demographicCategories.gender.map((gender) => (
              <TableCell key={`td-${gender.id}`} align="center">
                <NumberField
                  id={`field-gender-${gender.id}`}
                  name={`gender.${gender.id}`}
                  label=""
                  type="number"
                  control={form.control}
                  required
                  validation={validatePositiveInteger}
                  onKeyDown={preventNonNumericKeyPress}
                  data-cy="field-gender"
                />
              </TableCell>
            ))}
          </TableRow>
        </TableBody>
      </Table>

      <DataHeading variant="h4">
        RACE AND ETHNICITY
      </DataHeading>
      {demographicsMessages?.race && (
        <Alert severity="error" sx={{mb: 2}}>
          {demographicsMessages.race}
        </Alert>
      )}
      <Table sx={{
        width: 'auto'
      }} data-cy="table-race">
        <TableHead>
          <TableRow>
            {demographicCategories.raceEthnicity.map(race => (
              <TableHeadCell sx={{
                maxWidth: 175,
                padding: '4px 8px',
              }} key={`th-${race.id}`}>
                <label className={'required'} htmlFor={`race.${race.id}`}>{race.label}</label>
              </TableHeadCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          <TableRow>
            {demographicCategories.raceEthnicity.map(race => (
              <TableCell key={`td-${race.id}`} align="center">
                <NumberField
                  id={`field-race-${race.id}`}
                  name={`race.${race.id}`}
                  label=""
                  type="number"
                  control={form.control}
                  required
                  validation={validatePositiveInteger}
                  onKeyDown={preventNonNumericKeyPress}
                  data-cy="field-race"
                />
              </TableCell>
            ))}
          </TableRow>
        </TableBody>
      </Table>

      <Box mt="28px">
        <RadioButtonGroup
          id="field-source"
          name="source"
          label={'Who did you get the demographics from?'}
          control={form.control}
          fullWidth
          required
          options={demographicSources}
        />
      </Box>

      {showCompleteConfirmation && (
        <Box mt="28px">
          <CompleteEventFields form={form} />
        </Box>
      )}
    </Box>
  );
}
export default EventDemographicsFormFields;
